import { assetKindLabels, type AssetOs, type Release, type ReleaseAsset } from '@nexo/types';
import { buttonClasses, cn } from '@nexo/ui';
import { useQuery } from '@tanstack/react-query';
import { Download, Monitor, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatBytes } from '../../lib/format';
import { t } from '../../lib/i18n';

export const osLabels: Record<AssetOs, string> = {
  linux: 'Linux',
  windows: 'Windows',
  macos: 'macOS',
};

export function detectOs(): AssetOs | null {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('windows')) return 'windows';
  if (ua.includes('mac os') || ua.includes('macintosh')) return 'macos';
  if (ua.includes('linux') && !ua.includes('android')) return 'linux';
  return null;
}

/** Preferred download per OS: store first, then the most common installer. */
const preference: Record<AssetOs, string[]> = {
  linux: ['store', 'snap', 'deb', 'appimage', 'rpm'],
  windows: ['store', 'exe', 'msi'],
  macos: ['store', 'dmg'],
};

const archLabels: Record<string, string> = { aarch64: 'Apple Silicon', x86_64: 'Intel' };

const assetLabel = (asset: ReleaseAsset) => {
  if (asset.kind === 'store') return asset.label ?? t('Tienda');
  const arch =
    asset.arch === 'universal'
      ? ''
      : ` · ${asset.os === 'macos' ? (archLabels[asset.arch] ?? asset.arch) : asset.arch}`;
  return `${t(assetKindLabels[asset.kind])}${arch}`;
};

/**
 * Release notes as people read them: without commit trailers nor the «Versión 1.2.3» title line
 * (CI fills the notes from the tag's message, whose first line is that title).
 */
export const releaseNotes = (notes: string) =>
  notes
    .split('\n')
    .filter((line) => !/^(Co-Authored-By|Signed-off-by):/i.test(line.trim()))
    .filter((line, i) => !(i === 0 && /^versi[oó]n\s+v?\d+\.\d+\.\d+/i.test(line.trim())))
    .join('\n')
    .trim();

export function useLatestRelease() {
  return useQuery({ queryKey: ['releases', 'latest'], queryFn: () => api.releases.latest() });
}

/** Installers people download (the updater archive is only for the app itself). */
export const downloadableAssets = (release: Release | null | undefined) =>
  release?.assets.filter((a) => a.kind !== 'app-tar-gz') ?? [];

export function recommendedAsset(
  release: Release | null | undefined,
  os: AssetOs | null,
): ReleaseAsset | undefined {
  if (!os) return undefined;
  const assets = downloadableAssets(release);
  return preference[os]
    .map((kind) => assets.find((a) => a.os === os && a.kind === kind))
    .find(Boolean);
}

/** Big button with the best download for the visitor's system. */
export function RecommendedDownload({
  asset,
  os,
  variant = 'primary',
  className,
}: {
  asset: ReleaseAsset;
  os: AssetOs;
  variant?: 'primary' | 'secondary';
  className?: string;
}) {
  const { t } = useTranslation();
  const store = asset.kind === 'store';
  return (
    <a href={asset.url} className={cn(buttonClasses({ size: 'lg', variant }), className)}>
      {store ? <Store className="size-5" /> : <Download className="size-5" />}
      {store
        ? asset.label
          ? t('Conseguir en {{store}}', { store: asset.label })
          : t('Conseguir en la tienda')
        : t('Descargar para {{os}}', { os: osLabels[os] })}
    </a>
  );
}

/** Every installer, one card per operating system (the visitor's highlighted). */
export function PlatformDownloads({
  release,
  os,
}: {
  release: Release | null | undefined;
  os: AssetOs | null;
}) {
  const { t } = useTranslation();
  const assets = downloadableAssets(release);
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {(Object.keys(osLabels) as AssetOs[]).map((system) => {
        const mine = assets.filter((a) => a.os === system);
        return (
          <section
            key={system}
            className={cn(
              'rounded-2xl border bg-surface p-5 shadow-panel',
              system === os ? 'border-accent/50 ring-1 ring-accent/20' : 'border-border',
            )}
          >
            <h3 className="flex items-center gap-2 font-semibold">
              <Monitor className="size-4 text-muted" /> {osLabels[system]}
              {system === os && (
                <span className="ml-auto rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">
                  {t('Tu sistema')}
                </span>
              )}
            </h3>
            {mine.length === 0 ? (
              <p className="mt-3 text-sm text-subtle">{t('Próximamente')}</p>
            ) : (
              <ul className="mt-3 flex flex-col gap-1">
                {mine.map((asset) => (
                  <li key={asset.id}>
                    <a
                      href={asset.url}
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-surface-2"
                    >
                      {asset.kind === 'store' ? (
                        <Store className="size-4 text-accent" />
                      ) : (
                        <Download className="size-4 text-accent" />
                      )}
                      <span className="flex-1">{assetLabel(asset)}</span>
                      {asset.sizeBytes && (
                        <span className="text-xs text-subtle">{formatBytes(asset.sizeBytes)}</span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
